import { DEFAULT_SAS_COLORS } from './lineModel';
import type { SasColorRole } from './lineModel';
import { HEX_RE, mergePreferencePatch } from '../../theme/preferences';

export function isValidHexColor(value: string): boolean {
  return HEX_RE.test(value.trim());
}

/**
 * Reads `preferences.sasAnalyzer.colors` and keeps only entries that are a
 * known `SasColorRole` with a valid 6-digit hex value. Unknown roles, wrong
 * types, and malformed hex are dropped silently — a partially-written or
 * stale blob (e.g. from a future role added later, or from before this
 * key existed) can never break the page. Callers fill any missing role
 * from `DEFAULT_SAS_COLORS` (see `resolveSasAnalyzerColors`).
 */
export function parseSasAnalyzerColors(prefs: unknown): Partial<Record<SasColorRole, string>> {
  if (!prefs || typeof prefs !== 'object') return {};
  const sasAnalyzer = (prefs as Record<string, unknown>).sasAnalyzer;
  if (!sasAnalyzer || typeof sasAnalyzer !== 'object') return {};
  const colors = (sasAnalyzer as Record<string, unknown>).colors;
  if (!colors || typeof colors !== 'object') return {};

  const out: Partial<Record<SasColorRole, string>> = {};
  for (const role of Object.keys(DEFAULT_SAS_COLORS) as SasColorRole[]) {
    const value = (colors as Record<string, unknown>)[role];
    if (typeof value === 'string' && isValidHexColor(value)) {
      out[role] = value.trim();
    }
  }
  return out;
}

/** The palette to render with right now: stored overrides layered on the
 * Option-C defaults, one role at a time — never a partial/undefined role. */
export function resolveSasAnalyzerColors(prefs: unknown): Record<SasColorRole, string> {
  return { ...DEFAULT_SAS_COLORS, ...parseSasAnalyzerColors(prefs) };
}

/**
 * Read-merge-write helper for the SAS analyser's colour palette, built on
 * the shared `mergePreferencePatch` (`theme/preferences.ts`) — same
 * PUT-replaces-the-whole-object concern every `/me/preferences` writer
 * has, one implementation. This function's own job on top of the shared
 * shallow merge is the nested part: only `sasAnalyzer.colors` changes,
 * while any other key nested under `sasAnalyzer` (for future use) is
 * preserved rather than being dropped by a naive `{ sasAnalyzer: { colors } }`
 * overwrite. Never mutates `currentPrefs`.
 */
export function mergeSasAnalyzerColors(
  currentPrefs: unknown,
  colors: Record<SasColorRole, string>,
): Record<string, unknown> {
  const currentObj = currentPrefs && typeof currentPrefs === 'object'
    ? (currentPrefs as Record<string, unknown>)
    : {};
  const existingSasAnalyzer =
    currentObj.sasAnalyzer && typeof currentObj.sasAnalyzer === 'object'
      ? (currentObj.sasAnalyzer as Record<string, unknown>)
      : {};
  return mergePreferencePatch(currentPrefs, { sasAnalyzer: { ...existingSasAnalyzer, colors } });
}
